import dayjs from 'dayjs';
import { StandingOrderFrequency } from 'types/transactions.types';
import { getTransferType } from '../../helpers';
import type { TransferReviewData } from '../types';

/**
 * Creates the onConfirm function for transfer execution
 * Extracted from MoneyTransfer component to improve testability and reusability
 */
export const createTransferConfirmHandler = (
  reviewData: TransferReviewData,
  sourceAccountId?: string,
) => {
  if (!reviewData.execution?.mutateFunction) {
    console.error('Missing mutateFunction in reviewData.execution');
    return undefined;
  }

  return () => {
    const mutateFunction = reviewData.execution!.mutateFunction!;

    // Check if this is an internal transfer (no recipient IBAN/name)
    const isInternalTransfer =
      !reviewData.recipientIBAN && !reviewData.recipientName;

    if (isInternalTransfer) {
      // For internal transfers, we need the destination account ID
      // This should be passed through the review data or we need to get it from context
      // For now, let's assume it's in the originalFormData
      const originalFormData = reviewData.execution?.originalFormData || {};
      const destinationAccountId = originalFormData.toConnectorId;

      if (!destinationAccountId) {
        console.error('Missing destination account ID for internal transfer');
        return;
      }

      mutateFunction({
        fromConnectorId: sourceAccountId,
        toConnectorId: destinationAccountId,
        amount: { value: reviewData.amount, currency: 'EUR' },
        reason: 'transfer',
        type: 'internal',
      });
    } else {
      // External transfer logic
      // Check if it's a regular payment (OneTime period with today's date)
      const firstExecutionDateIsToday = dayjs(
        reviewData.schedule.firstExecutionDate,
      ).isSame(dayjs(), 'day');

      const isRegularPayment =
        reviewData.schedule.period === StandingOrderFrequency.OneTime &&
        firstExecutionDateIsToday;

      // Prepare standing order properties based on the current review data
      const standingOrderProps = isRegularPayment
        ? {}
        : reviewData.schedule.period === StandingOrderFrequency.OneTime
        ? {
            requestedExecutionAt: dayjs(
              reviewData.schedule.firstExecutionDate,
            ).toISOString(),
          }
        : {
            period: reviewData.schedule.period,
            firstExecutionDate: dayjs(
              reviewData.schedule.firstExecutionDate,
            ).toISOString(),
            lastExecutionDate: reviewData.schedule.lastExecutionDate
              ? dayjs(reviewData.schedule.lastExecutionDate).toISOString()
              : undefined,
          };

      const originalFormData = reviewData.execution!.originalFormData!;

      mutateFunction({
        fromConnectorId: sourceAccountId,
        communication: {
          message: reviewData.communication,
          // todo: attention here. We need to type all of this fields
          type: originalFormData.communication_type || 'unstructured',
        },
        amount: { value: reviewData.amount, currency: 'EUR' },
        reason: 'transfer',
        beneficiary: {
          iban: reviewData.recipientIBAN,
          name: reviewData.recipientName,
          // Include verification token for external transfers if available
          verificationToken: originalFormData.verificationToken,
        },
        type: getTransferType(
          originalFormData.instant,
          reviewData.schedule.period as StandingOrderFrequency,
        ),
        ...standingOrderProps,
      });
    }
  };
};

/**
 * Updates schedule data in review data and recreates the onConfirm handler
 */
export const updateTransferSchedule = (
  reviewData: TransferReviewData,
  scheduleData: {
    period: StandingOrderFrequency;
    firstExecutionDate: Date;
    lastExecutionDate?: Date;
  },
  sourceAccountId?: string,
): TransferReviewData => {
  const updatedReviewData = {
    ...reviewData,
    schedule: {
      period: scheduleData.period,
      firstExecutionDate: scheduleData.firstExecutionDate,
      lastExecutionDate: scheduleData.lastExecutionDate,
      requestedExecutionAt:
        scheduleData.period === StandingOrderFrequency.OneTime
          ? scheduleData.firstExecutionDate
          : undefined,
    },
  };

  // Recreate onConfirm function with updated data
  const onConfirm = createTransferConfirmHandler(
    updatedReviewData,
    sourceAccountId,
  );

  return {
    ...updatedReviewData,
    execution: {
      ...updatedReviewData.execution,
      onConfirm,
    },
  };
};
